import React from 'react';
import { TextField, Button, Grid } from '@mui/material';
import { PrimaryButton, OutlinedTextInput, Text, Colors } from 'library';
import { BusinessOutlaw, BusinessOutlawLocation } from 'models';

type banHandler = (location: any, business: any) => any;
type unBanHandler = (businessOutlawId: number) => any;
type setMemo = (memo: string) => void;

type MemoProps = {
    resource: any;
    onChange: (value: string) => any;
    showText: boolean;
};

export function MemoField({ resource, onChange, showText }: MemoProps) {
    return showText ? (
        <Text variant="body2">
            <span>
                <strong>Memo: </strong>
            </span>
            {resource.memo ? resource.memo : 'N/A'}
        </Text>
    ) : (
        <TextField label="Memo/Notes" variant="standard" onChange={(e) => onChange(e.target.value)} />
    );
}

type BanBtnProps = {
    business: BusinessOutlaw;
    location?: BusinessOutlawLocation;
    banHandler: banHandler;
    unBanHandler: unBanHandler;
};

export function BanButton({ business, location, banHandler, unBanHandler }: BanBtnProps) {
    let resource = location ?? business;

    function clickHandler() {
        if (resource && resource.business_outlaw_id) {
            unBanHandler(resource.business_outlaw_id);
        } else if (resource && !resource.business_outlaw_id) {
            banHandler(location?.id, business.id);
        }
    }
    return (
        <Button variant="outlined" onClick={() => clickHandler()}>
            {resource.business_outlaw_id ? 'Un-' : ''}Block
        </Button>
    );
}

type BusinessSearchProps = {
    searchHandler: (value: null) => any;
    setNameOrId: any;
};

export function BusinessSearch({ searchHandler, setNameOrId }: BusinessSearchProps) {
    return (
        <Grid container style={{ marginBottom: '2em' }}>
            <Grid item sm={6}>
                <OutlinedTextInput label="Business ID/Name" onChange={(e) => setNameOrId(e.target.value)} />
            </Grid>
            <Grid item sm={6}>
                <PrimaryButton onClick={searchHandler}>Search</PrimaryButton>
            </Grid>
        </Grid>
    );
}

export function UserInfo({ user, outlawed }: { user: any; outlawed: BusinessOutlaw[] }) {
    const isOutlawedSomewhere = outlawed.some((o) => o.business_outlaw_id !== null);
    return (
        <Grid style={{ marginBottom: '2em' }}>
            <Text variant="caption" textStyle={{ marginBottom: 10 }}>
                You can block/un-block workers from being booked business wide or at specific locations using the
                options below.
            </Text>
            <Text>
                {!isOutlawedSomewhere ? 'Block ' : ''}
                <Text inline variant="h2" textStyle={{ color: Colors.turquoise }}>
                    {user.first_name} {user.last_name}
                </Text>{' '}
                {!isOutlawedSomewhere ? 'from:' : 'has been blocked from:'}
            </Text>
        </Grid>
    );
}

type BusinessListProps = {
    outlawed: BusinessOutlaw[];
    setMemo?: setMemo;
    banHandler: banHandler;
    unBanHandler: unBanHandler;
    showCreatedBy?: boolean;
};

export function BusinessList({ outlawed, setMemo, banHandler, unBanHandler, showCreatedBy = true }: BusinessListProps) {
    return (
        <Grid container direction="column">
            {outlawed.length ? (
                outlawed.map((business) => (
                    <Grid
                        item
                        key={business.id}
                        style={{
                            background: Colors.lightTurq,
                            marginBottom: '1em',
                            borderRadius: '10px',
                            padding: '1em',
                        }}
                    >
                        <Grid style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text variant="h2" bold>
                                {business.name}
                            </Text>
                            <BanButton unBanHandler={unBanHandler} banHandler={banHandler} business={business} />
                        </Grid>
                        <Grid item style={{ marginBottom: '1em' }}>
                            {setMemo ? (
                                <MemoField
                                    resource={business}
                                    onChange={setMemo}
                                    showText={!!business.business_outlaw_id}
                                />
                            ) : null}
                        </Grid>
                        {showCreatedBy ? (
                            <Grid item style={{ marginBottom: '1em' }}>
                                <Text variant="body2">
                                    <strong>Created by: </strong> {business.created_by}
                                </Text>
                            </Grid>
                        ) : null}
                        <BusinessLocationList
                            business={business}
                            setMemo={setMemo}
                            banHandler={banHandler}
                            unBanHandler={unBanHandler}
                        />
                    </Grid>
                ))
            ) : (
                <Text>No Businesses Found</Text>
            )}
        </Grid>
    );
}

type BusinessLocationListProps = {
    business: BusinessOutlaw;
    setMemo?: setMemo;
    banHandler: banHandler;
    unBanHandler: unBanHandler;
};

export function BusinessLocationList({ business, setMemo, banHandler, unBanHandler }: BusinessLocationListProps) {
    return !business.business_outlaw_id ? (
        <>
            <Text bold>Locations:</Text>
            <Grid style={{ marginLeft: '1em' }}>
                {business.business_locations.map((location: any) => (
                    <Grid key={location.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Grid style={{ marginBottom: '2em' }}>
                            <Text>{location.name || 'No location name found'}</Text>
                            <Text
                                variant="body2"
                                color={Colors.mediumGrey}
                            >{`${location.address.street}, ${location.address.city}, ${location.address.state}`}</Text>
                            {setMemo ? (
                                <MemoField
                                    resource={location}
                                    onChange={setMemo}
                                    showText={location.business_outlaw_id || business.business_outlaw_id}
                                />
                            ) : null}
                        </Grid>
                        <Grid item>
                            <BanButton
                                unBanHandler={unBanHandler}
                                banHandler={banHandler}
                                business={business}
                                location={location}
                            />
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        </>
    ) : null;
}
